import _ from 'lodash';
import Style from './loadResource.module.scss';
import { useCallback, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { getFileExtension } from 'src/helpers/file-helper';
import { AppAttachmentsField } from 'uikit/fields';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';
import { ConfirmResult, ModalResult } from 'src/components/dialogModal/useConfirmationModal';
import { useModal } from 'src/application/hooks/useModal';
import { ValidationFormRules } from 'src/helpers/validation-helpers';
import { LayoutAttachmentType } from 'src/features/generalForms/context/attachments.context';
import { useNode } from '@craftjs/core';

const FILE_TYPES = ['.jpg', '.jpeg', '.png'];

type FormState = {
  attachment: AppAttachmentValue[] | null;
  isEditModal: boolean;
};

export const useUploadPicture = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.Panels.ImageBlock');
  const { id: nodeId } = useNode();
  const { openModal, closeModal, visible } = useModal('CLOSED');
  const promiseRef = useRef<(test: ModalResult<LayoutAttachmentType>) => void>(null!);
  const form = useForm<FormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const start = useCallback(
    async (attachment?: LayoutAttachmentType[]) => {
      if (attachment) {
        form.setValue('isEditModal', !!attachment);
        form.setValue(
          'attachment',
          attachment.map(
            (a) =>
              ({
                file: a.file,
                fileName: a.fileName,
                id: a.fileId,
              } satisfies AppAttachmentValue),
          ),
        );
      }
      openModal();

      return new Promise<ModalResult<LayoutAttachmentType>>((resolve) => {
        promiseRef.current = resolve;
      });
    },
    [form, openModal],
  );

  const onSubmit = form.handleSubmit((data: FormState) => {
    const { attachment } = data;
    const firstAttachment = _.first(attachment);
    if (!firstAttachment) return;

    if (firstAttachment.file && !FILE_TYPES.includes(getFileExtension(firstAttachment.fileName))) {
      form.setError('root', { message: t('Error') });
      return;
    }

    promiseRef?.current?.({
      result: ConfirmResult.Confirm,
      data: {
        file: firstAttachment.file!,
        fileName: firstAttachment.fileName,
        nodeId: nodeId,
        fileId: null,
      } satisfies LayoutAttachmentType,
    });
    closeModal();
  });

  const onModalClose = useCallback(() => {
    form.reset();
    closeModal();
    promiseRef.current?.({ result: ConfirmResult.Closed });
  }, [closeModal, form]);

  const modalHeader = useMemo(() => {
    return form.watch('isEditModal') ? t('EditHeader') : t('AddHeader');
  }, [form, t]);

  return {
    open: start,
    modal: (
      <DialogModal
        onHide={onModalClose}
        visible={visible}
        title={modalHeader}
        bodyClassName={Style.modalBody}
        footer={{
          leftButton: {
            colorSchema: 'secondary',
            text: t('Modal.Cancel'),
            onClick: onModalClose,
            disabled: form.formState.isSubmitting,
          },
          rightButton: {
            colorSchema: 'primary',
            text: t('Modal.Save'),
            onClick: onSubmit,
            isLoading: form.formState.isSubmitting,
            disabled: form.formState.isSubmitting,
          },
        }}
      >
        <Controller
          control={form.control}
          name={'attachment'}
          rules={{ ...ValidationFormRules().requiredRule }}
          render={({ field: { value, onChange }, fieldState }) => (
            <AppAttachmentsField
              fileTypes={FILE_TYPES}
              label={t('Modal.FileFieldHeader')}
              onChange={onChange}
              value={value}
              errorText={fieldState.error?.message}
            />
          )}
        />
      </DialogModal>
    ),
  };
};
